import { CameraControls } from '@react-three/drei';
import { invalidate, useFrame, useThree } from '@react-three/fiber';
import { RefObject, useCallback, useEffect, useRef } from 'react';

import { updatePreviewCamera } from 'app/modules/wallforming/components/scenes/camera-controls/utils/cameraUtils';

interface PreviewCameraHandlerProps {
    cameraControlsRef: RefObject<CameraControls | null>;
}

export const usePreviewCameraHandler = ({ cameraControlsRef }: PreviewCameraHandlerProps) => {
    const gl = useThree((state) => state.gl);
    const camera = useThree((state) => state.camera);
    const size = useThree((state) => state.size);
    const previousZoom = useRef<number | undefined>(undefined);
    const syncAnimationFrameRef = useRef<number | null>(null);
    const resizeRendererRef = useRef<boolean>(false);

    const scheduleCameraUpdate = useCallback(
        (resizeRenderer = false) => {
            resizeRendererRef.current ||= resizeRenderer;

            if (syncAnimationFrameRef.current !== null) {
                cancelAnimationFrame(syncAnimationFrameRef.current);
            }

            syncAnimationFrameRef.current = window.requestAnimationFrame(() => {
                syncAnimationFrameRef.current = null;

                if (resizeRendererRef.current) {
                    resizeRendererRef.current = false;
                    gl.setSize(size.width, size.height);
                }

                updatePreviewCamera(gl.domElement, cameraControlsRef);
                invalidate();
            });
        },
        [gl, size.height, size.width, cameraControlsRef]
    );

    // Handle camera update and canvas viewport on resize events
    useEffect(() => {
        const resizeObserver = new ResizeObserver(() => {
            scheduleCameraUpdate(true);
        });

        resizeObserver.observe(gl.domElement);

        return () => {
            resizeObserver.disconnect();

            if (syncAnimationFrameRef.current !== null) {
                cancelAnimationFrame(syncAnimationFrameRef.current);
                syncAnimationFrameRef.current = null;
            }
        };
    }, [gl.domElement, scheduleCameraUpdate]);

    // Handle camera update on zoom change
    useFrame(() => {
        if (previousZoom.current !== camera.zoom) {
            previousZoom.current = camera.zoom;
            scheduleCameraUpdate(false);
        }
    });
};
